import { request } from "./http";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;

  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.items?.content)) return obj.items.content;
  if (Array.isArray(obj.item?.items)) return obj.item.items;
  if (Array.isArray(obj.item?.content)) return obj.item.content;
  if (Array.isArray(obj.item)) return obj.item;
  return [];
}

function extractPage(raw) {
  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj;
  if (obj.items && typeof obj.items === "object" && Array.isArray(obj.items.content)) return obj.items;
  if (obj.item && typeof obj.item === "object" && Array.isArray(obj.item.content)) return obj.item;
  return { content: extractItems(raw), totalElements: extractItems(raw).length };
}

function buildPageResult(pageLike, fallbackPage = 0, fallbackSize = 20) {
  const content = extractItems(pageLike);
  const page = Number.isFinite(Number(pageLike?.number)) ? Number(pageLike.number) : fallbackPage;
  const size = Number.isFinite(Number(pageLike?.size)) ? Number(pageLike.size) : fallbackSize;
  const totalElements = Number.isFinite(Number(pageLike?.totalElements))
    ? Number(pageLike.totalElements)
    : content.length;
  const totalPages = Number.isFinite(Number(pageLike?.totalPages))
    ? Number(pageLike.totalPages)
    : Math.max(1, Math.ceil(totalElements / Math.max(1, size)));

  return {
    page,
    size,
    totalElements,
    totalPages,
    hasMore: page + 1 < totalPages,
  };
}

export function normalizePlannerTask(item = {}) {
  const raw = asObject(item);
  const status = String(raw.status || raw.state || "").toUpperCase();
  const kind = String(raw.kind || raw.type || raw.category || "").trim();
  const priority = String(raw.priority || "").trim();
  const done = Boolean(raw.completedAt) || status === "DONE" || status === "COMPLETED" || status === "FINISHED";

  return {
    id: raw.id ?? raw.taskId ?? null,
    ownerUserId: raw.ownerUserId ?? null,
    assignedByPsychologistUserId: raw.assignedByPsychologistUserId ?? null,
    title: String(raw.title || raw.name || "Задача"),
    description: String(raw.description || raw.text || "").trim(),
    dueAt: raw.dueAt || raw.dueDate || raw.date || null,
    completedAt: raw.completedAt || null,
    status,
    kind,
    category: kind,
    priority,
    done,
    editableByOwner: raw.editableByOwner !== false,
  };
}

export const plannerApi = {
  async listTasks({ kind = "", page = 0, size = 50 } = {}) {
    const search = new URLSearchParams();
    search.set("page", String(Math.max(0, Number(page) || 0)));
    search.set("size", String(Math.max(1, Number(size) || 50)));
    if (String(kind || "").trim()) search.set("kind", String(kind).trim());

    const response = await request(`/api/me/planner/tasks?${search.toString()}`);
    const pageLike = extractPage(response?.items ?? response);
    const meta = buildPageResult(pageLike, page, size);

    return {
      ...meta,
      items: asArray(pageLike?.content).map(normalizePlannerTask),
    };
  },

  async completeTask(taskId) {
    const id = String(taskId ?? "").trim();
    if (!id) throw new Error("taskId is required");
    const response = await request(`/api/me/planner/tasks/${encodeURIComponent(id)}/complete`, { method: "POST" });
    return normalizePlannerTask(response?.item ?? response);
  },

  async reopenTask(taskId) {
    const id = String(taskId ?? "").trim();
    if (!id) throw new Error("taskId is required");
    const response = await request(`/api/me/planner/tasks/${encodeURIComponent(id)}/reopen`, { method: "POST" });
    return normalizePlannerTask(response?.item ?? response);
  },
};
